import React, { useEffect, useRef, useMemo } from 'react';
import {
  Network,
  Building,
  Phone,
  MapPin,
  User,
  Link2
} from 'lucide-react';
import { Badge } from './shared/Badge';

interface CarrierNode {
  dotNumber: number;
  carrierName: string;
  status?: string;
  statusCode?: string;
  hasViolations?: boolean;
  hasBadEvents?: boolean;
}

interface CarrierRelationship {
  fromDot: number;
  toDot: number;
  sameOwner: boolean;
  sameAddress: boolean;
  samePhone: boolean;
  relationshipStrength: number;
}

interface CarrierRelationshipGraphProps {
  carriers: CarrierNode[];
  phase2Indicators: {
    relatedCarriers: boolean;
    sameOwner: boolean;
    sameAddress: boolean;
    samePhone: boolean;
    recentlyCreatedCarriers: boolean;
  };
  onCarrierClick?: (dotNumber: number) => void;
}

// Simple network visualization without external dependencies
// Uses SVG for rendering
export const CarrierRelationshipGraph: React.FC<CarrierRelationshipGraphProps> = ({
  carriers,
  phase2Indicators,
  onCarrierClick
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  // Deduplicate carriers by DOT number to prevent duplicate key errors
  const uniqueCarriers = useMemo(() => {
    const seen = new Set<number>();
    return carriers.filter(carrier => {
      if (seen.has(carrier.dotNumber)) {
        return false;
      }
      seen.add(carrier.dotNumber);
      return true;
    });
  }, [carriers]);

  // Calculate node positions in a circular layout
  const nodePositions = useMemo(() => {
    const positions: Record<number, { x: number; y: number }> = {};
    const centerX = 200;
    const centerY = 150;
    const radius = uniqueCarriers.length <= 2 ? 80 : 100;

    uniqueCarriers.forEach((carrier, idx) => {
      if (uniqueCarriers.length === 1) {
        positions[carrier.dotNumber] = { x: centerX, y: centerY };
      } else {
        const angle = (2 * Math.PI * idx) / uniqueCarriers.length - Math.PI / 2;
        positions[carrier.dotNumber] = {
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle),
        };
      }
    });

    return positions;
  }, [uniqueCarriers]);

  // Build edges based on phase2 indicators (simplified - connect all carriers if related)
  const edges = useMemo(() => {
    const result: Array<{
      from: number;
      to: number;
      types: string[];
      strength: number;
    }> = [];

    if (!phase2Indicators.relatedCarriers || uniqueCarriers.length < 2) {
      return result;
    }

    // Create edges between consecutive carriers if relationships detected
    for (let i = 0; i < uniqueCarriers.length - 1; i++) {
      const types: string[] = [];
      let strength = 0;

      if (phase2Indicators.sameOwner) {
        types.push('owner');
        strength += 40;
      }
      if (phase2Indicators.sameAddress) {
        types.push('address');
        strength += 30;
      }
      if (phase2Indicators.samePhone) {
        types.push('phone');
        strength += 20;
      }

      if (types.length > 0) {
        result.push({
          from: uniqueCarriers[i].dotNumber,
          to: uniqueCarriers[i + 1].dotNumber,
          types,
          strength,
        });
      }
    }

    return result;
  }, [uniqueCarriers, phase2Indicators]);

  // Get node color based on status and events
  const getNodeColor = (carrier: CarrierNode): string => {
    if (carrier.hasBadEvents || carrier.statusCode === 'O') {
      return '#b3412c'; // red
    }
    if (carrier.hasViolations || carrier.statusCode === 'I') {
      return '#b07d2b'; // yellow/orange
    }
    return '#23211d'; // blue
  };

  // Get edge color based on relationship type
  const getEdgeColor = (types: string[]): string => {
    if (types.includes('owner')) return '#b3412c'; // red for same owner
    if (types.includes('address')) return '#b07d2b'; // orange for same address
    if (types.includes('phone')) return '#23211d'; // blue for same phone
    return '#a39d90'; // gray default
  };

  if (uniqueCarriers.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500">
        <Network className="w-12 h-12 mx-auto mb-2 text-gray-400" />
        <p>No carrier relationships to display</p>
      </div>
    );
  }

  const hasRelationships = edges.length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
            <Network className="w-5 h-5 text-purple-600" />
            Carrier Relationship Network
          </h4>
          <div className="flex items-center gap-2">
            <Badge variant="info" size="sm">{uniqueCarriers.length} carriers</Badge>
            {hasRelationships && (
              <Badge variant="warning" size="sm">{edges.length} connections</Badge>
            )}
          </div>
        </div>
      </div>

      {/* SVG Network Graph */}
      <div className="p-4">
        <svg
          ref={svgRef}
          width="400"
          height="300"
          viewBox="0 0 400 300"
          className="w-full max-w-md mx-auto"
        >
          {/* Edges */}
          {edges.map((edge, idx) => {
            const from = nodePositions[edge.from];
            const to = nodePositions[edge.to];
            if (!from || !to) return null;

            const edgeColor = getEdgeColor(edge.types);
            const strokeWidth = Math.max(2, edge.strength / 20);

            return (
              <g key={`edge-${idx}`}>
                <line
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke={edgeColor}
                  strokeWidth={strokeWidth}
                  strokeDasharray={edge.types.includes('owner') ? '' : '5,5'}
                  opacity={0.7}
                />
                {/* Edge label */}
                <text
                  x={(from.x + to.x) / 2}
                  y={(from.y + to.y) / 2 - 8}
                  textAnchor="middle"
                  fontSize="10"
                  fill={edgeColor}
                  className="font-semibold"
                >
                  {edge.types.map(t => t === 'owner' ? 'Owner' : t === 'address' ? 'Addr' : 'Phone').join(', ')}
                </text>
              </g>
            );
          })}

          {/* Nodes */}
          {uniqueCarriers.map((carrier) => {
            const pos = nodePositions[carrier.dotNumber];
            if (!pos) return null;

            const nodeColor = getNodeColor(carrier);

            return (
              <g
                key={carrier.dotNumber}
                transform={`translate(${pos.x}, ${pos.y})`}
                className="cursor-pointer"
                onClick={() => onCarrierClick?.(carrier.dotNumber)}
              >
                {/* Node circle */}
                <circle
                  r="24"
                  fill={nodeColor}
                  stroke="white"
                  strokeWidth="3"
                  className="transition-all hover:opacity-80"
                />
                {/* Building icon placeholder */}
                <text
                  textAnchor="middle"
                  dy="5"
                  fill="#fbf9f4"
                  fontSize="12"
                  fontWeight="bold"
                >
                  DOT
                </text>

                {/* Carrier label */}
                <text
                  y="40"
                  textAnchor="middle"
                  fontSize="11"
                  fill="#4a463f"
                  className="font-medium"
                >
                  {carrier.carrierName.length > 18
                    ? carrier.carrierName.substring(0, 15) + '...'
                    : carrier.carrierName}
                </text>
                <text
                  y="52"
                  textAnchor="middle"
                  fontSize="9"
                  fill="#857f73"
                >
                  DOT {carrier.dotNumber}
                </text>

                {/* Status indicator */}
                {carrier.statusCode && carrier.statusCode !== 'A' && (
                  <circle
                    cx="18"
                    cy="-18"
                    r="6"
                    fill={carrier.statusCode === 'O' ? '#b3412c' : '#b07d2b'}
                    stroke="white"
                    strokeWidth="2"
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Relationship indicators detail */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className={`flex items-center gap-2 p-2 rounded ${phase2Indicators.sameOwner ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-500'}`}>
            <User className="w-4 h-4" />
            <span>Same Owner</span>
            {phase2Indicators.sameOwner && <Badge variant="danger" size="sm">Yes</Badge>}
          </div>
          <div className={`flex items-center gap-2 p-2 rounded ${phase2Indicators.sameAddress ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-500'}`}>
            <MapPin className="w-4 h-4" />
            <span>Same Address</span>
            {phase2Indicators.sameAddress && <Badge variant="warning" size="sm">Yes</Badge>}
          </div>
          <div className={`flex items-center gap-2 p-2 rounded ${phase2Indicators.samePhone ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
            <Phone className="w-4 h-4" />
            <span>Same Phone</span>
            {phase2Indicators.samePhone && <Badge variant="info" size="sm">Yes</Badge>}
          </div>
          <div className={`flex items-center gap-2 p-2 rounded ${phase2Indicators.recentlyCreatedCarriers ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-500'}`}>
            <Building className="w-4 h-4" />
            <span>New Carriers</span>
            {phase2Indicators.recentlyCreatedCarriers && <Badge variant="default" size="sm">Yes</Badge>}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border-t border-gray-200">
        <div className="flex items-center gap-4 text-xs text-gray-600 flex-wrap">
          <span className="font-semibold">Node Colors:</span>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span>Active/Clean</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span>Violations/Inactive</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span>Bad Events/OOS</span>
          </div>
          <span className="font-semibold ml-2">Connections:</span>
          <div className="flex items-center gap-1">
            <div className="w-6 h-0.5 bg-red-500" />
            <span>Owner</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-0.5 bg-orange-500 border-dashed" style={{ borderStyle: 'dashed' }} />
            <span>Address</span>
          </div>
        </div>
      </div>

      {/* No relationships message */}
      {!hasRelationships && uniqueCarriers.length > 1 && (
        <div className="px-4 py-3 bg-green-50 border-t border-green-200">
          <div className="flex items-center gap-2 text-green-700">
            <Link2 className="w-4 h-4" />
            <span className="text-sm">No carrier relationships detected - carriers appear to be independent entities</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierRelationshipGraph;
